/**
 * Form Configuration Constants
 * Global configuration for form behavior
 */

export const FORM_CONFIG = {
  // ============ TIMING ============
  
  /** Debounce time for search operations (milliseconds) */
  SEARCH_DEBOUNCE_TIME: 300,
  
  /** Debounce time for value change events (milliseconds) */
  VALUE_CHANGE_DEBOUNCE_TIME: 300,
  
  /** Debounce time for auto-save operations (milliseconds) */
  AUTO_SAVE_DEBOUNCE_TIME: 1000,
  
  /** Delay before showing loading indicator (milliseconds) */
  LOADING_INDICATOR_DELAY: 500,
  
  // ============ SEARCH ============
  
  /** Minimum characters required to trigger search */
  MIN_SEARCH_LENGTH: 3,
  
  /** Maximum number of search results to display */
  MAX_SEARCH_RESULTS: 100,
  
  /** Maximum number of items to display before showing "load more" */
  MAX_ITEMS_PER_PAGE: 50,
  
  // ============ FILE UPLOAD ============
  
  /** Maximum file size in bytes (5MB) */
  MAX_FILE_SIZE: 5 * 1024 * 1024,
  
  /** Maximum file size for images in bytes (2MB) */
  MAX_IMAGE_SIZE: 2 * 1024 * 1024,
  
  /** Maximum file size for documents in bytes (10MB) */
  MAX_DOCUMENT_SIZE: 10 * 1024 * 1024,
  
  /** Allowed image file extensions */
  ALLOWED_IMAGE_TYPES: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'],
  
  /** Allowed document file extensions */
  ALLOWED_DOCUMENT_TYPES: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'],
  
  /** Allowed generic file extensions */
  ALLOWED_FILE_TYPES: ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt'],
  
  // ============ VALIDATION ============
  
  /** Email validation pattern */
  EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  
  /** Phone validation pattern (10 digits) */
  PHONE_PATTERN: /^[0-9]{10}$/,
  
  /** URL validation pattern */
  URL_PATTERN: /^https?:\/\/.+/,
  
  /** Default error messages */
  DEFAULT_ERROR_MESSAGES: {
    required: 'This field is required',
    email: 'Invalid email format',
    pattern: 'Invalid format',
    min: 'Value is too small',
    max: 'Value is too large',
    minlength: 'Too short',
    maxlength: 'Too long',
  },
  
  // ============ API ============
  
  /** Number of retry attempts for failed API calls */
  API_RETRY_COUNT: 3,
  
  /** API request timeout in milliseconds */
  API_TIMEOUT: 30000,
  
  /** Delay between retry attempts (milliseconds) */
  API_RETRY_DELAY: 1000,
  
  // ============ FORM BEHAVIOR ============
  
  /** Enable/disable form validation on blur */
  VALIDATE_ON_BLUR: true,
  
  /** Enable/disable form validation on change */
  VALIDATE_ON_CHANGE: false,
  
  /** Show validation errors immediately or on submit */
  SHOW_ERRORS_IMMEDIATELY: false,
  
  /** Auto-focus first field on form load */
  AUTO_FOCUS_FIRST_FIELD: true,
  
  /** Clear form after successful submission */
  CLEAR_FORM_ON_SUBMIT: false,
  
  // ============ JSEXECUTOR ============
  
  /** Maximum execution time for JavaScript code (milliseconds) */
  JS_EXECUTION_TIMEOUT: 5000,
  
  /** Enable console logging in JSExecutor */
  JS_ENABLE_CONSOLE: true,
  
  /** Allowed global objects in JSExecutor sandbox */
  JS_ALLOWED_GLOBALS: ['Math', 'Date', 'Number', 'String', 'Boolean', 'console'],
  
  // ============ UI ============
  
  /** Show field labels */
  SHOW_LABELS: true,
  
  /** Show required field indicators */
  SHOW_REQUIRED_INDICATORS: true,
  
  /** Show field help text */
  SHOW_HELP_TEXT: true,
  
  /** Show character count for text fields */
  SHOW_CHARACTER_COUNT: false,
  
  /** Animation duration for field transitions (milliseconds) */
  ANIMATION_DURATION: 200,
  
  // ============ DEFAULTS ============
  
  /** Default date format */
  DEFAULT_DATE_FORMAT: 'yyyy-MM-dd',
  
  /** Default datetime format */
  DEFAULT_DATETIME_FORMAT: 'yyyy-MM-dd HH:mm:ss',
  
  /** Default time format */
  DEFAULT_TIME_FORMAT: 'HH:mm',
  
  /** Default number of decimal places */
  DEFAULT_DECIMAL_PLACES: 2,
  
  /** Default currency code */
  DEFAULT_CURRENCY: 'INR',
  
  /** Default locale */
  DEFAULT_LOCALE: 'en-IN',
} as const;

/**
 * Type-safe access to form configuration
 */
export type FormConfigKey = keyof typeof FORM_CONFIG;

/**
 * Get configuration value by key
 */
export function getFormConfig<K extends FormConfigKey>(key: K): typeof FORM_CONFIG[K] {
  return FORM_CONFIG[key];
}
